/* eslint-disable jsx-a11y/control-has-associated-label, react/jsx-one-expression-per-line */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Text, Box, InputGroup, InputLeftElement, Input, MenuButton, Menu, HStack } from '@chakra-ui/core';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import { getTranslatedValue } from '../../../utils/localizedString';
import { getTimeZoneOffset } from '../../../utils/timezoneUtils';
import { formatTimeZones } from '../../../app/constants/timezones';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';

export interface TimezoneValue {
  name: string;
  offset: string;
}

export interface TimezoneSelectProps {
  value: TimezoneValue;
  onChange: (value: TimezoneValue) => void;
  label: string;
  id: string;
  errorMessage?: string;
  isDisabled?: boolean;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(tzName: string, locale: string): Intl.DateTimeFormat {
  const key = `${locale}::${tzName}`;
  if (!formatterCache.has(key)) {
    formatterCache.set(
      key,
      new Intl.DateTimeFormat(locale, {
        timeZone: tzName,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    );
  }
  return formatterCache.get(key)!;
}

function formatLocalTime(tzName: string): string {
  if (!tzName) return '';
  try {
    return getFormatter(tzName, i18next.language).format(new Date());
  } catch {
    /* istanbul ignore next */
    return '';
  }
}

function useLocalTime(tzName: string): string {
  const [time, setTime] = useState<string>(() => formatLocalTime(tzName));

  const tzRef = useRef(tzName);
  tzRef.current = tzName;

  useEffect(() => {
    setTime(formatLocalTime(tzName));
    if (!tzName) return undefined;

    const msUntilNextMinute = (60 - new Date().getSeconds()) * 1000;
    let interval: ReturnType<typeof setInterval>;

    const timeout = setTimeout(() => {
      setTime(formatLocalTime(tzRef.current));
      interval = setInterval(/* istanbul ignore next */ () => setTime(formatLocalTime(tzRef.current)), 60_000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [tzName]);

  return time;
}

export const TimezoneSelect = memo(function TimezoneSelect({
  value,
  onChange,
  label,
  id,
  errorMessage,
  isDisabled = false,
}: TimezoneSelectProps) {
  const { t } = useTranslation('calendar');
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const localTime = useLocalTime(value.name);
  const { currentUserProfile } = useCurrentUserProfile();

  const primaryTimezone = currentUserProfile?.primaryTimezone;
  const secondaryTimezone = currentUserProfile?.secondaryTimezone;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const tzOptions = useMemo(
    () =>
      formatTimeZones.map((tz) => ({
        value: tz.value,
        label: getTranslatedValue(tz.label, i18next.language),
        gmt: tz.gmt,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18next.language],
  );

  const sortedTzOptions = useMemo(
    () => {
      const primary = primaryTimezone?.name ? tzOptions.find((tz) => tz.value === primaryTimezone.name) : undefined;

      const secondary = secondaryTimezone?.name ? tzOptions.find((tz) => tz.value === secondaryTimezone.name) : undefined;

      const pinnedValues = new Set([primary?.value, secondary?.value].filter(Boolean));

      const rest = tzOptions.filter((tz) => !pinnedValues.has(tz.value));

      return { primary, secondary, rest };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tzOptions, primaryTimezone?.name, secondaryTimezone?.name],
  );

  return (
    <Box id={id} data-testid="timezoneSelect">
      <Text mb="2">{label}</Text>
      <Box position="relative" ref={containerRef}>
        <Menu autoSelect={false} isLazy>
          <>
            <MenuButton
              w="full"
              px={4}
              py={2}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              isDisabled={isDisabled}
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <HStack justify="space-between">
                <Text color="gray.600">
                  {`${tzOptions.find((tz) => tz.value === value.name)?.gmt} ${
                    tzOptions.find((tz) => tz.value === value.name)?.label
                  }` || /* istanbul ignore next */ label}
                </Text>
                <ChevronDownIcon
                  transition="transform 0.2s"
                  transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                  color="gray.400"
                />
              </HStack>
            </MenuButton>

            {isOpen && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={1401}
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="md"
                mt={1}
              >
                <Box px={3} py={2} bg="white" position="sticky" borderRadius="md" top={0} zIndex={1}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.650" />
                    </InputLeftElement>
                    <Input
                      type="text"
                      placeholder={t('availabilityForm.timezoneSelectSearchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="neutral.200"
                      bg="neutral.100"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </InputGroup>
                </Box>
                <Box maxH="260px" overflowY="auto">
                  {(() => {
                    const filtered = (list: typeof tzOptions) =>
                      list.filter((tz) => tz.label.toLowerCase().includes(searchTerm.toLowerCase()));

                    const pinnedItems = [sortedTzOptions.primary, sortedTzOptions.secondary].filter(
                      (tz): tz is NonNullable<typeof tz> => tz !== undefined,
                    );
                    const filteredPinned = filtered(pinnedItems);
                    const filteredRest = filtered(sortedTzOptions.rest);
                    const showDivider = filteredPinned.length > 0 && filteredRest.length > 0;

                    const renderItem = (tz: typeof tzOptions[number]) => (
                      <Box
                        key={tz.value}
                        px={4}
                        py={2}
                        cursor="pointer"
                        sx={tz.value === value.name ? { bg: 'gray.100' } : {}}
                        _hover={{ bg: 'gray.100' }}
                        onClick={() => {
                          onChange({ name: tz.value, offset: getTimeZoneOffset(tz.value) });
                          setIsOpen(false);
                          setSearchTerm('');
                        }}
                      >
                        <Text fontSize="sm">
                          {tz.gmt} {tz.label}
                        </Text>
                      </Box>
                    );

                    return (
                      <>
                        {filteredPinned.map(renderItem)}
                        {showDivider && <Box borderTopWidth="1px" borderColor="gray.200" my={1} />}
                        {filteredRest.map(renderItem)}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            )}
          </>
        </Menu>
      </Box>

      {errorMessage && (
        <Text fontSize="xs" color="red.500" data-testid="form-error-message">
          {errorMessage}
        </Text>
      )}

      {localTime && (
        <Text fontSize="12px" color="gray.500" mt={1}>
          {`${t('availabilityForm.timezoneLocalTime')} ${value.name}`}: <strong>{localTime}</strong>
        </Text>
      )}
    </Box>
  );
});

export default TimezoneSelect;
